import { RedirectUrls } from '@cannect/constants/texts'
import { buildFilterUrl } from '@cannect/constants/productFilters'
import Modal from 'new-components/Modal'
import { useHistory } from 'react-router'

export type TUnavailableProduct = {
  product_id: number
  name: string | null
  /** Classe do produto capturada do carrinho antes da remoção — usada no filtro do "Buscar produto". */
  class_id?: number | null
}

export interface UnavailableProductModalProps {
  isOpen: boolean
  products: TUnavailableProduct[]
  /** Fecha o modal sem prosseguir (clique no X / overlay). */
  onClose: () => void
  /** "Continuar para pagamento" — segue o fluxo com o carrinho já atualizado. */
  onContinue: () => void
  /** Desabilita os botões enquanto o carrinho ainda está sendo atualizado. */
  isLoading?: boolean
}

const buildProductsLabel = (products: TUnavailableProduct[]) => {
  const names = products.map((product) => product.name).filter(Boolean) as string[]

  if (names.length === 0) return 'que estava em seu carrinho'
  if (names.length === 1) return `${names[0]} que estava em seu carrinho`

  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]} que estavam em seu carrinho`
}

export const UnavailableProductModal = ({
  isOpen,
  products,
  onClose,
  onContinue,
  isLoading = false
}: UnavailableProductModalProps) => {
  const history = useHistory()

  const isPlural = products.length > 1
  const productsLabel = buildProductsLabel(products)

  const goToProducts = () => {
    const classIds = Array.from(
      new Set(products.map((product) => product.class_id).filter((classId): classId is number => Number(classId) > 0))
    )

    history.push(
      buildFilterUrl({
        translated_name: '',
        ...(classIds.length > 0 ? { class_id: classIds } : {})
      })
    )
  }

  const goToWhatsapp = () => {
    window.open(RedirectUrls.WHATSAPP_UNAVAILABLE_PRODUCT, '_blank', 'noopener,noreferrer')
  }

  return (
    <Modal isOpen={isOpen} setOpenModal={onClose} title="Produto indisponível">
      <div className="flex flex-col gap-6 px-2 py-4 sm:px-4">
        <p className="text-sm leading-relaxed text-gray-700 sm:text-base">
          Desculpe, o {isPlural ? 'Produto(s)' : 'Produto'}{' '}
          <strong className="font-semibold text-gray-900">{productsLabel}</strong>{' '}
          {isPlural ? 'ficaram indisponíveis' : 'ficou indisponível'} no momento e precisamos{' '}
          {isPlural ? 'removê-los' : 'removê-lo'}. Gostaríamos de ajudá-lo a encontrar uma alternativa. Você pode
          explorar produtos similares em nosso site ou falar com um de nossos atendentes que terão prazer em sugerir a
          melhor opção para você.
        </p>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={goToProducts}
            disabled={isLoading}
            className="w-full rounded-md border-none bg-primary-700 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:bg-gray-400">
            Buscar produto
          </button>

          <button
            type="button"
            onClick={goToWhatsapp}
            disabled={isLoading}
            className="w-full rounded-md border border-solid border-primary-700 bg-transparent px-4 py-3 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400">
            Falar com atendente
          </button>

          <button
            type="button"
            onClick={onContinue}
            disabled={isLoading}
            className="w-full rounded-md border-none bg-transparent px-4 py-3 text-sm font-medium text-gray-600 underline transition-colors hover:text-gray-800 disabled:cursor-not-allowed disabled:text-gray-400">
            {isLoading ? 'Atualizando carrinho...' : 'Continuar para pagamento'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default UnavailableProductModal
